/**
 * HeroSection - Responsive hero display for home screen
 *
 * Handles different display modes:
 * - Web default: Simplified hero with subtitle only (80px)
 * - Web keyboard: Hidden entirely (StickyHeader shows branding)
 * - Native default: Full logo + pill + subtitle
 * - Native keyboard: Compact header with small logo
 *
 * Extracted from index.tsx to simplify conditional rendering.
 */
import { View, Platform, StyleSheet } from "react-native";
import { TierBody } from "@dramadetector/ui";
import { Logo } from "../../Logo";
import { GlassPill } from "../../glass";
import { useTranslation } from "@dramadetector/shared";

interface HeroSectionProps {
  /** Whether the virtual keyboard is visible */
  isKeyboardVisible: boolean;
}

export function HeroSection({ isKeyboardVisible }: HeroSectionProps) {
  const { t } = useTranslation("home");
  const isWeb = Platform.OS === "web";

  // Web keyboard visible: Hide hero entirely (StickyHeader shows branding)
  if (isWeb && isKeyboardVisible) {
    return null;
  }

  // Web default: Simplified hero with just subtitle (80px section)
  if (isWeb) {
    return (
      <View style={styles.heroSection}>
        <TierBody size="sm" style={styles.heroSubtitle}>
          {t("home.subtitle")}
        </TierBody>
      </View>
    );
  }

  // Native keyboard visible: Compact header with small logo
  if (isKeyboardVisible) {
    return (
      <View style={styles.heroSectionNative}>
        <View style={styles.headerCompactContent}>
          <Logo size="sm" />
          <TierBody size="sm" style={styles.subtitleCompact}>
            DramaDetector
          </TierBody>
        </View>
      </View>
    );
  }

  // Native default: Full logo + pill + subtitle
  return (
    <View style={styles.heroSectionNative}>
      <Logo size="lg" style={{ marginBottom: 16 }} />
      <GlassPill translationKey="app_name" namespace="common" size="lg" />
      <TierBody size="base" style={styles.subtitle}>
        {t("home.subtitle")}
      </TierBody>
    </View>
  );
}

const styles = StyleSheet.create({
  heroSection: {
    alignItems: "center",
    height: 80,
    justifyContent: "center",
    marginBottom: 24,
  },
  heroSectionNative: {
    alignItems: "center",
    marginBottom: 32,
  },
  heroSubtitle: {
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: 14,
  },
  headerCompactContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subtitle: {
    marginTop: 8,
    textAlign: "center",
    color: "#FFFFFF",
    opacity: 0.9,
  },
  subtitleCompact: {
    textAlign: "center",
    color: "#FFFFFF",
    fontWeight: "700",
    opacity: 0.95,
  },
});
